import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Users, Save, History, AlertTriangle, Loader2, CheckCircle2,
  Upload, ShoppingCart, Factory, UserCheck, MinusCircle, RefreshCw, AlertCircle,
} from 'lucide-react';
import { SellerGroup, HistoryEntry, PROFILE_OPTIONS } from './types';
import { fetchSellerGroups, batchUpdateSellerGroups, fetchSellerGroupHistory } from './api';
import { StatsCard } from './StatsCard';
import { SearchInput } from './SearchInput';
import { SellerGroupTable } from './SellerGroupTable';
import { HistoryModal } from './HistoryModal';
import { ImportModal } from './ImportModal';
import { PageTitle } from '../../../../_metronic/layout/core';

const PROFILE_CARD_CONFIG: Record<string, { icon: React.ReactNode; label: string }> = {
  'Compra Agora': { icon: <ShoppingCart className="w-5 h-5" />, label: 'Compra Agora' },
  'Indústria': { icon: <Factory className="w-5 h-5" />, label: 'Indústria' },
  'Seller': { icon: <UserCheck className="w-5 h-5" />, label: 'Seller' },
  '': { icon: <MinusCircle className="w-5 h-5" />, label: 'Sem Perfil' },
};

const SellerGroupScreen: React.FC = () => {
  const [groups, setGroups] = useState<SellerGroup[]>([]);
  const [editedProfiles, setEditedProfiles] = useState<Map<string, string>>(new Map());
  const [searchTerm, setSearchTerm] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<HistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadGroups = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await fetchSellerGroups();
      setGroups(data);
    } catch {
      setLoadError('Erro ao carregar os grupos. Verifique a conexão e tente novamente.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const filteredGroups = useMemo(() => {
    if (!searchTerm) return groups;
    const term = searchTerm.toLowerCase();
    return groups.filter(
      (g) =>
        g.id.toLowerCase().includes(term) ||
        g.name.toLowerCase().includes(term) ||
        g.profile.toLowerCase().includes(term)
    );
  }, [groups, searchTerm]);

  const profileCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    PROFILE_OPTIONS.forEach((p) => { counts[p] = 0; });
    groups.forEach((g) => {
      const profile = editedProfiles.get(g.id) ?? g.profile;
      counts[profile] = (counts[profile] || 0) + 1;
    });
    return counts;
  }, [groups, editedProfiles]);

  const handleProfileChange = useCallback(
    (groupId: string, newProfile: string) => {
      const original = groups.find((g) => g.id === groupId);
      if (!original) return;

      const next = new Map(editedProfiles);
      if (newProfile === original.profile) {
        next.delete(groupId);
      } else {
        next.set(groupId, newProfile);
      }
      setEditedProfiles(next);
      setSaveSuccess(false);
      setSaveError(null);
    },
    [groups, editedProfiles]
  );

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    const items = Array.from(editedProfiles.entries()).map(([groupId, profile]) => ({
      group_id: groupId,
      profile,
    }));

    try {
      const res = await batchUpdateSellerGroups(items);

      if (!res.success) {
        const errors: string[] = [];
        if (res.results) {
          res.results
            .filter((r) => !r.ok)
            .forEach((r) => {
              errors.push(`Grupo ${r.group_id ?? items[r.index]?.group_id}: ${r.error || 'Erro desconhecido'}`);
            });
        }
        if (errors.length === 0) {
          errors.push(res.error || 'Erro ao salvar as alterações');
        }
        setSaveError(errors.join('; '));
      } else {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch {
      setSaveError('Falha na conexão com o servidor');
    }

    setEditedProfiles(new Map());
    setIsSaving(false);
    await loadGroups();
  };

  const handleOpenHistory = useCallback(async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryData([]);
    try {
      const entries = await fetchSellerGroupHistory();
      setHistoryData(entries);
    } catch {
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleImportComplete = async () => {
    setImportOpen(false);
    await loadGroups();
  };

  const changesCount = editedProfiles.size;

  return (
    <>
      <PageTitle>Seller Group</PageTitle>
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <header className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                  Gerencie os perfis dos grupos de vendedores
                </h1>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => setImportOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all"
                >
                  <Upload className="w-4 h-4" />
                  <span className="hidden sm:inline">Importar</span>
                </button>
                <button
                  onClick={handleOpenHistory}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all"
                >
                  <History className="w-4 h-4" />
                  <span className="hidden sm:inline">Histórico</span>
                </button>
                <button
                  onClick={handleSave}
                  disabled={changesCount === 0 || isSaving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-blue-600/20 disabled:shadow-none"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Salvar Alterações
                    </>
                  )}
                </button>
              </div>
            </div>

            {changesCount > 0 && (
              <div className="mt-5 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
                <div className="bg-amber-100 p-1.5 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                </div>
                <p className="text-sm font-medium text-amber-800">
                  {changesCount} {changesCount === 1 ? 'grupo com alteração pendente' : 'grupos com alterações pendentes'}
                </p>
              </div>
            )}

            {saveSuccess && (
              <div className="mt-5 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3">
                <div className="bg-emerald-100 p-1.5 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                </div>
                <p className="text-sm font-medium text-emerald-800">
                  Alterações salvas com sucesso
                </p>
              </div>
            )}

            {saveError && (
              <div className="mt-5 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                <div className="bg-red-100 p-1.5 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                </div>
                <p className="text-sm font-medium text-red-800">{saveError}</p>
              </div>
            )}
          </header>

          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 mb-8">
            <StatsCard
              title="Total de Grupos"
              value={groups.length}
              description="Grupos cadastrados"
              icon={<Users className="w-5 h-5" />}
            />
            <StatsCard
              title="Alterações Pendentes"
              value={changesCount}
              description={changesCount === 0 ? 'Nenhuma alteração' : 'Aguardando salvar'}
              icon={<AlertTriangle className="w-5 h-5" />}
              variant={changesCount > 0 ? 'warning' : 'default'}
            />
            {PROFILE_OPTIONS.map((profile) => {
              const config = PROFILE_CARD_CONFIG[profile];
              if (!config) return null;
              return (
                <StatsCard
                  key={profile || '__empty__'}
                  title={config.label}
                  value={profileCounts[profile] || 0}
                  description={`${profileCounts[profile] || 0} grupo${(profileCounts[profile] || 0) !== 1 ? 's' : ''}`}
                  icon={config.icon}
                />
              );
            })}
          </div>

          <div className="mb-5">
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Buscar por ID, nome ou perfil..."
            />
          </div>

          {loadError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex flex-col items-center gap-3 mb-5">
              <AlertCircle className="w-8 h-8 text-red-400" />
              <p className="text-sm text-red-700 text-center">{loadError}</p>
              <button
                onClick={loadGroups}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Tentar novamente
              </button>
            </div>
          )}

          {isLoading && !loadError && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-16 flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              <p className="text-sm text-gray-500">Carregando grupos...</p>
            </div>
          )}

          {!isLoading && !loadError && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <SellerGroupTable
                data={filteredGroups}
                editedProfiles={editedProfiles}
                onProfileChange={handleProfileChange}
              />
            </div>
          )}
        </div>
      </div>

      <HistoryModal
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        history={historyData}
        loading={historyLoading}
      />

      <ImportModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImportComplete={handleImportComplete}
        currentGroups={groups}
      />
    </>
  );
};

export default SellerGroupScreen;
